// GEOINT-kartlag (GEO-08): viser FERDIGE fotogrammetriprodukter (GEO-03/04:
// ortofoto/DSM/DTM/termisk fra NodeODM-køen) som togglbare rasterlag, med
// nøyaktighet PÅ laget — ikke i en tooltip et sted operatøren må lete.
//
// ── HVORFOR DETTE SKILLER SEG FRA basemaps.ts ───────────────────────────────
// Basiskartene er en FAST liste kjent ved kompilering. GEOINT-produkter er
// det motsatte: hvilke som finnes endrer seg etter hvilke jobber som er
// ferdige og har fått fliser (`POST /geoint/jobb/fliser`), og et produkt
// utenfor operatørens org-scope skal ALDRI vises — hubben filtrerer allerede
// alt dette i `GET /geoint/jobber` (se `server.ts`), så klienten viser
// rått det den fikk, uten en egen skyggekopi av tilgangslogikken.
//
// ── UX_DESIGNSYSTEM: FORBEHOLDET STÅR VED TALLET ────────────────────────────
// Eierfunn 2026-08-17: et forbehold om et tall skal stå VED tallet, ikke et
// annet sted i UI-et. GSD og georeferering vises derfor i SAMME rad som
// forbeholdene fra `produktForbehold()` — allerede beregnet av hubben og
// levert som `Geojobb.forbehold` i API-svaret, ikke regnet på nytt her.
//
// ── GEO-09: JOBBER SOM IKKE ER FERDIGE ENNÅ ─────────────────────────────────
// `GET /geoint/jobber` returnerer ALLE jobber i køen (se `server.ts`), ikke
// bare de ferdige — men fram til nå filtrerte laget dem bort før operatøren
// fikk se dem. En jobb i kø, en som kjører (med eller uten kjent framdrift),
// eller en som feilet, er reell status — ikke fravær av status. Se
// `services/hub/src/geokoe.ts` (`Jobbstatus`/`JOBBSTATUS_TEKST`) for den
// autoritative statusmodellen; tekstene her SKAL matche dem ordrett.
//
// ⚠ `framdrift === null` ER IKKE 0 %. `Geojobb.framdrift`s egen kommentar i
// `geokoe.ts` sier det rett ut: mangler noden et framdriftstall, vet vi ikke —
// og en fremdriftslinje tegnet på 0 % er en påstand om at ingenting har
// skjedd. Vi viser «fremdrift ukjent» i stedet for å late som vi vet.
import type { Fotogrammetriprodukt, FotogrammetriType } from '@skade/contracts';
import type { Map as MlMap } from 'maplibre-gl';
import { icon } from '../ui/icons.js';

/**
 * Statustekster for GEOINT-jobber — SAMME ORDLYD som `JOBBSTATUS_TEKST` i
 * `services/hub/src/geokoe.ts` (den autoritative kilden). Duplisert her, ikke
 * importert: hubben er en server-pakke, COP er nettleserkode, og de to har
 * ingen delt avhengighet for denne teksten i dag. Endres ordlyden ett sted,
 * skal den endres begge steder.
 */
export const GEOINT_JOBBSTATUS_TEKST: Record<string, string> = {
  koe: 'I kø — venter på noden',
  kjorer: 'Kjører på noden',
  ferdig: 'Ferdig — produkt tilgjengelig',
  feilet: 'FEILET — se feilmeldingen',
  avbrutt: 'Avbrutt av operatør',
  ukjent: '⚠ UKJENT — noden svarte ikke sist. Jobben kan fortsatt kjøre; ikke start den på nytt før noden er sjekket.',
};

/** Statuser som fortsatt kan avbrytes av en operatør (matcher hubbens `avbryt()`). */
const AVBRYTBARE_STATUSER = new Set(['koe', 'kjorer', 'ukjent']);

/** Ett element fra `GET /geoint/jobber`. Bare feltene laget faktisk bruker. */
export interface GeointJobbRad {
  id: string;
  status: string;
  navn?: string;
  produkt?: Fotogrammetriprodukt;
  /** Forbeholdene til produktet — beregnet av hubben (`produktForbehold()`), ikke her. */
  forbehold?: string[];
  /** Filnavn under hubbens flislager — satt når `POST /geoint/jobb/fliser` er kjørt. */
  flisFil?: string;
  /**
   * Framdrift 0–100, eller `null` for UKJENT. `undefined` når hubben ikke har
   * satt feltet (bakoverkompatibelt svar) — behandles likt som `null`.
   */
  framdrift?: number | null;
  /** Feilmeldingen fra noden, ordrett — satt kun når `status === 'feilet'`. */
  feil?: string;
}

export interface GeointJobberSvar {
  jobber: GeointJobbRad[];
}

export interface GeointLagOpts {
  hent: () => Promise<GeointJobberSvar | null>;
  varsle: (t: string) => void;
  /**
   * Avbryt en jobb i kø/kjørende (`POST /geoint/jobb/avbryt` i hubben).
   * Valgfri: uten denne vises jobbene, men uten avbryt-knapp.
   */
  avbryt?: (jobbId: string) => Promise<void>;
}

/**
 * Kun jobber med et FAKTISK servérbart flislag.
 *
 * ⚠ TRE VILKÅR, IKKE ÉN SJEKK. `ferdig` alene holder ikke: et FERDIG produkt
 * uten opplastede fliser (GEO-16/17 — automatisk utpakking er ikke bygget,
 * se `geokoe.ts`) har ingenting å vise, og et produkt-felt uten selve
 * produktet ville krasjet rendring i stedet for å forklare hvorfor det ikke
 * kan vises.
 */
export function visbareGeointLag(jobber: readonly GeointJobbRad[]): GeointJobbRad[] {
  return jobber.filter((j) => j.status === 'ferdig' && j.produkt && j.flisFil);
}

/**
 * Jobber som IKKE er ferdige ennå: i kø, kjørende, feilet, avbrutt eller
 * ukjent (noden svarte ikke). Operatøren skal se disse — en snurrende sirkel
 * i 40 minutter uten et ord er ikke en status (GEO-09).
 *
 * ⚠ ALT SOM IKKE ER `ferdig` TELLES MED HER — også en `ferdig`-jobb som
 * mangler `flisFil`/`produkt` (GEO-16, ikke bygget ennå) blir IKKE dratt inn
 * i denne lista: den er ferdig, bare uten et servérbart kartlag. Det er en
 * annen mangel enn å fortsatt jobbe eller ha feilet, og skal ikke blandes inn
 * i «pågår»-lista.
 */
export function paagaaendeGeointJobber(jobber: readonly GeointJobbRad[]): GeointJobbRad[] {
  return jobber.filter((j) => j.status !== 'ferdig');
}

/** Lesbar statustekst — samme ordlyd som hubbens `JOBBSTATUS_TEKST`. */
export function geointStatusTekst(status: string): string {
  return GEOINT_JOBBSTATUS_TEKST[status] ?? status;
}

/** Fremdriftstekst: aldri en falsk 0 % når `framdrift` er ukjent. */
export function geointFramdriftTekst(framdrift: number | null | undefined): string {
  if (framdrift === null || framdrift === undefined) return 'fremdrift ukjent';
  return `${Math.round(framdrift)} %`;
}

/** Kan denne jobben avbrytes akkurat nå (matcher hubbens `Geokoe.avbryt()`)? */
export function kanAvbrytesGeointJobb(status: string): boolean {
  return AVBRYTBARE_STATUSER.has(status);
}

/** MapLibre-URL-malen for ett produkts flislag (se `mbtiles.ts`/`server.ts`). */
export function geointFlisUrl(jobbId: string): string {
  return `/geoint/fliser/${encodeURIComponent(jobbId)}/{z}/{x}/{y}.png`;
}

/** MapLibre-kilde-id og lag-id for en jobb — samlet ett sted så de ikke kan skilles. */
export function geointKildeId(jobbId: string): string {
  return `geoint-src-${jobbId}`;
}
export function geointLagId(jobbId: string): string {
  return `geoint-lag-${jobbId}`;
}

/** Tidsvinduet formatert norsk — to tall, aldri ett tidspunkt (se `fotogrammetri.ts`). */
export function geointTidsvindu(fraMs: number, tilMs: number): string {
  const f = new Date(fraMs);
  const t = new Date(tilMs);
  const dato = (d: Date) => d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const kl = (d: Date) => d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  return dato(f) === dato(t)
    ? `${dato(f)} ${kl(f)}–${kl(t)}`
    : `${dato(f)} ${kl(f)} – ${dato(t)} ${kl(t)}`;
}

export const GEOINT_TYPE_TEKST: Record<FotogrammetriType, string> = {
  ortofoto: 'Ortofoto',
  dsm: 'Overflatemodell (DSM)',
  dtm: 'Terrengmodell (DTM)',
  termisk: 'Termisk mosaikk',
  objekthoyde: 'Objekthøyde',
};

export const GEOREFERERING_TEKST: Record<string, string> = {
  gcp: 'bakkekontrollpunkter (desimeter)',
  gnss: 'kun GNSS (meter — verre under jamming)',
  ukjent: 'ukjent metode',
};

/** Diff mellom forrige og nåværende synlige lag — hvilke MapLibre-kilder som må inn/ut. */
export interface GeointLagDiff {
  leggTil: string[];
  fjern: string[];
}
export function geointLagDiff(forrige: ReadonlySet<string>, naa: ReadonlySet<string>): GeointLagDiff {
  return {
    leggTil: [...naa].filter((id) => !forrige.has(id)),
    fjern: [...forrige].filter((id) => !naa.has(id)),
  };
}

/**
 * GEOINT-kartlagspanelet: liste over ferdige produkter, med av/på-hake per
 * produkt. Rene beregninger (over) er testet uavhengig av DOM/MapLibre.
 */
export class GeointLag {
  readonly el: HTMLElement;
  private listeEl!: HTMLElement;
  private statusEl!: HTMLElement;
  /** Seksjonen for jobber som IKKE er ferdige — kø/kjører/feilet/avbrutt/ukjent. */
  private paagaarLabelEl!: HTMLElement;
  private paagaarListeEl!: HTMLElement;
  private jobber: GeointJobbRad[] = [];
  private paagaaende: GeointJobbRad[] = [];
  /** Jobb-id-er operatøren har slått PÅ. Overlever et `oppfrisk()`. */
  private synlige = new Set<string>();
  onStateChange?: () => void;

  constructor(private readonly map: MlMap, private readonly opts: GeointLagOpts) {
    this.el = this.bygg();
  }

  get open(): boolean {
    return this.el.classList.contains('open');
  }

  toggle(): boolean {
    const aapen = this.el.classList.toggle('open');
    this.onStateChange?.();
    if (aapen) void this.oppfrisk();
    return aapen;
  }

  async oppfrisk(): Promise<void> {
    let svar: GeointJobberSvar | null;
    try {
      svar = await this.opts.hent();
    } catch {
      svar = null;
    }
    if (!svar) {
      // ⚠ EN HENTEFEIL ER IKKE «INGEN PRODUKTER». Samme prinsipp som kanaloversikten.
      this.jobber = [];
      this.statusEl.textContent = '⚠ Kunne ikke lese GEOINT-produkter fra hubben.';
      this.statusEl.classList.add('advarsel');
      this.listeEl.replaceChildren();
      return;
    }
    this.jobber = visbareGeointLag(svar.jobber);
    // GEO-09: jobber i kø/kjørende/feilet/avbrutt/ukjent — samme svar, samme
    // `oppfrisk()`, ingen egen pollingløsning (se filhodet).
    this.paagaaende = paagaaendeGeointJobber(svar.jobber);
    // Et produkt som forsvant fra lista (utløpt scope, fjernet av eier) skal ikke
    // fortsette å ligge på kartet som et lag ingen kan slå av lenger.
    const naaIder = new Set(this.jobber.map((j) => j.id));
    for (const id of [...this.synlige]) {
      if (!naaIder.has(id)) { this.synlige.delete(id); this.fjernLag(id); }
    }
    this.statusEl.classList.remove('advarsel');
    this.statusEl.textContent = this.jobber.length
      ? `${this.jobber.length} produkt(er) tilgjengelig`
      : 'Ingen ferdige GEOINT-produkter med fliser ennå.';
    this.tegn();
    this.tegnPaagaaende();
  }

  private settSynlig(jobbId: string, synlig: boolean): void {
    if (synlig) this.synlige.add(jobbId); else this.synlige.delete(jobbId);
    if (synlig) this.leggTilLag(jobbId); else this.fjernLag(jobbId);
  }

  private leggTilLag(jobbId: string): void {
    const jobb = this.jobber.find((j) => j.id === jobbId);
    if (!jobb?.produkt) return;
    const kilde = geointKildeId(jobbId);
    const lag = geointLagId(jobbId);
    if (this.map.getSource(kilde)) return; // alt lagt til
    const p = jobb.produkt;
    this.map.addSource(kilde, {
      type: 'raster',
      tiles: [geointFlisUrl(jobbId)],
      tileSize: 256,
      // ⚠ BEGRENSER SPØRRINGER TIL PRODUKTETS FAKTISKE DEKNING. Uten `bounds`
      // ville MapLibre bedt om fliser over hele verden — og fått 404 for alle
      // utenfor flyvningen, som er riktig svar, men bortkastede kall.
      bounds: [p.minLon, p.minLat, p.maxLon, p.maxLat],
    });
    this.map.addLayer({ id: lag, type: 'raster', source: kilde, paint: { 'raster-opacity': 0.92 } });
  }

  private fjernLag(jobbId: string): void {
    const lag = geointLagId(jobbId);
    const kilde = geointKildeId(jobbId);
    if (this.map.getLayer(lag)) this.map.removeLayer(lag);
    if (this.map.getSource(kilde)) this.map.removeSource(kilde);
  }

  private bygg(): HTMLElement {
    const p = document.createElement('aside');
    p.className = 'geoint-panel';

    const head = document.createElement('div');
    head.className = 'geoint-head';
    const t = document.createElement('span');
    t.className = 'section-label';
    t.textContent = 'GEOINT-lag';
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'geoint-x';
    x.textContent = '×';
    x.onclick = () => { this.el.classList.remove('open'); this.onStateChange?.(); };
    head.append(t, x);
    p.appendChild(head);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'geoint-status';
    this.statusEl.setAttribute('role', 'status');
    p.appendChild(this.statusEl);

    this.listeEl = document.createElement('div');
    this.listeEl.className = 'geoint-liste';
    p.appendChild(this.listeEl);

    // ── GEO-09: jobber som IKKE er ferdige ennå ───────────────────────────
    // Egen seksjon under produktlista — aldri blandet sammen med de ferdige,
    // som er togglbare kartlag. En jobb i kø har ikke et lag å slå på.
    this.paagaarLabelEl = document.createElement('div');
    this.paagaarLabelEl.className = 'geoint-paagaar-label section-label';
    this.paagaarLabelEl.textContent = 'Pågående/ikke-ferdige jobber';
    this.paagaarLabelEl.hidden = true;
    p.appendChild(this.paagaarLabelEl);

    this.paagaarListeEl = document.createElement('div');
    this.paagaarListeEl.className = 'geoint-paagaar-liste';
    p.appendChild(this.paagaarListeEl);

    return p;
  }

  private tegn(): void {
    this.listeEl.replaceChildren();
    if (!this.jobber.length) return;
    for (const j of this.jobber) this.listeEl.appendChild(this.rad(j));
  }

  private rad(j: GeointJobbRad): HTMLElement {
    const p = j.produkt!;
    const rad = document.createElement('div');
    rad.className = 'geoint-rad';

    const topp = document.createElement('label');
    topp.className = 'geoint-rad-topp';
    const hake = document.createElement('input');
    hake.type = 'checkbox';
    hake.checked = this.synlige.has(j.id);
    hake.onchange = () => this.settSynlig(j.id, hake.checked);
    const navn = document.createElement('span');
    navn.className = 'geoint-navn';
    navn.textContent = `${p.navn} — ${GEOINT_TYPE_TEKST[p.type]}`;
    topp.append(hake, navn);
    rad.appendChild(topp);

    const meta = document.createElement('div');
    meta.className = 'geoint-meta';
    // ⚠ GSD OG GEOREFERERING I SAMME LINJE — forbeholdet ved tallet.
    meta.textContent = `${p.gsdCm.toFixed(1)} cm/px · ${GEOREFERERING_TEKST[p.georeferering] ?? p.georeferering} `
      + `· ${geointTidsvindu(p.fraMs, p.tilMs)}`;
    rad.appendChild(meta);

    // Kilde: motor+flagg ordrett — reproduserbarhet, se `produktForbehold()`.
    // Mangler `motor`, sier forbeholdslisten det allerede (ikke gjentatt her).
    if (p.motor) {
      const kilde = document.createElement('div');
      kilde.className = 'geoint-kilde';
      kilde.textContent = p.motor;
      rad.appendChild(kilde);
    }

    if (j.forbehold?.length) {
      const forb = document.createElement('div');
      forb.className = 'geoint-forbehold';
      for (const f of j.forbehold) {
        const linje = document.createElement('div');
        linje.textContent = f;
        forb.appendChild(linje);
      }
      rad.appendChild(forb);
    }

    return rad;
  }

  private tegnPaagaaende(): void {
    this.paagaarListeEl.replaceChildren();
    this.paagaarLabelEl.hidden = this.paagaaende.length === 0;
    for (const j of this.paagaaende) this.paagaarListeEl.appendChild(this.radPaagaaende(j));
  }

  private radPaagaaende(j: GeointJobbRad): HTMLElement {
    const rad = document.createElement('div');
    rad.className = 'geoint-paagaar-rad';
    if (j.status === 'feilet') rad.classList.add('feilet');
    if (j.status === 'ukjent') rad.classList.add('ukjent');

    const topp = document.createElement('div');
    topp.className = 'geoint-paagaar-topp';
    const navn = document.createElement('span');
    navn.className = 'geoint-navn';
    navn.textContent = j.navn || j.produkt?.navn || j.id;
    topp.appendChild(navn);

    if (this.opts.avbryt && kanAvbrytesGeointJobb(j.status)) {
      const avbrytKnapp = document.createElement('button');
      avbrytKnapp.type = 'button';
      avbrytKnapp.className = 'geoint-avbryt';
      avbrytKnapp.textContent = 'Avbryt';
      avbrytKnapp.onclick = () => void this.avbrytJobb(j.id);
      topp.appendChild(avbrytKnapp);
    }
    rad.appendChild(topp);

    const status = document.createElement('div');
    status.className = 'geoint-paagaar-status';
    status.textContent = geointStatusTekst(j.status);
    rad.appendChild(status);

    // ⚠ ALDRI EN FALSK 0 %-STREK. `framdrift === null`/`undefined` → tekst,
    // ikke en fylt-til-0-linje som påstår at ingenting har skjedd.
    if (j.status === 'kjorer' || j.status === 'koe') {
      const framdrift = document.createElement('div');
      framdrift.className = 'geoint-paagaar-framdrift';
      if (typeof j.framdrift === 'number') {
        const bar = document.createElement('div');
        bar.className = 'geoint-framdrift-bar';
        const fyll = document.createElement('div');
        fyll.className = 'geoint-framdrift-fyll';
        fyll.style.width = `${Math.max(0, Math.min(100, j.framdrift))}%`;
        bar.appendChild(fyll);
        framdrift.appendChild(bar);
        const tekst = document.createElement('span');
        tekst.textContent = geointFramdriftTekst(j.framdrift);
        framdrift.appendChild(tekst);
      } else {
        framdrift.textContent = geointFramdriftTekst(j.framdrift);
      }
      rad.appendChild(framdrift);
    }

    // Den FAKTISKE feilmeldingen — «feilet» alene forklarer ingenting.
    if (j.status === 'feilet' && j.feil) {
      const feil = document.createElement('div');
      feil.className = 'geoint-paagaar-feil';
      feil.textContent = j.feil;
      rad.appendChild(feil);
    }

    return rad;
  }

  /**
   * Avbryt en jobb i kø/kjørende. Bekreftelse er hovedappens ansvar (`ui/shell.ts`
   * sin `bekreft()`), ikke dette laget: `map/`-filer avhenger ikke av `ui/`-filer
   * (se filhodet), så `opts.avbryt` er allerede den bekreftede handlingen når
   * den kalles hit.
   */
  private async avbrytJobb(jobbId: string): Promise<void> {
    if (!this.opts.avbryt) return;
    try {
      await this.opts.avbryt(jobbId);
      this.opts.varsle('Jobben ble avbrutt.');
    } catch (e) {
      this.opts.varsle(`⚠ Kunne ikke avbryte jobben: ${e instanceof Error ? e.message : String(e)}`);
    }
    await this.oppfrisk();
  }
}

/** Dock-ikonet for GEOINT-lag-panelet. */
export const GEOINT_IKON = icon('layers');
